import React, { useState, useEffect, useMemo } from 'react';
import { Grid } from '@mui/material';
import moment from 'moment';
import QRCode from 'react-qr-code';
import { useApi } from '../ContextApi/ContextApi';
import '../UI/Template.css';

const Template2 = ({ reportData = [], QRurl }) => {
  const [fontStyle, setFontStyle] = useState({});
  const currentDateTime = useMemo(() => new Date(), []);

  const username = `${sessionStorage.getItem("FirstName") || ""} ${sessionStorage.getItem("LastName") || ""}`.trim();
  const { Comment, isheader } = useApi();

  useEffect(() => {
    try {
      const fonts = JSON.parse(localStorage.getItem('FontStyle'));
      if (fonts) {
        // Handle both object and array formats safely
        setFontStyle(Array.isArray(fonts) ? fonts[0] || {} : fonts);
      }
    } catch (e) {
      console.error("Failed to parse FontStyle from localStorage", e);
    }
  }, []);

  // Group and sort report data efficiently using useMemo
  const groupedData = useMemo(() => {
    if (!Array.isArray(reportData)) return {};

    return reportData.reduce((result, item) => {
      const category = item.categorY_CODE || 'GEN';
      const subCategory = item.suB_CATEGORY_CODE || 'MAIN';

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE || 'General Panel',
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE || '',
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  }, [reportData]);

  // Safe formatting helper for line breaks
  const formatTextWithLineBreaks = (text) => {
    if (!text) return '-';
    const lines = String(text).split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const image = localStorage.getItem('LogoImage');
  const patient = reportData?.[0] || {};
  const labName = useMemo(() => {
    return reportData.find((x) => x.laboratoryName)?.laboratoryName || "Clinical Laboratory";
  }, [reportData]);

  const qrCodeValue = QRurl || '';
  const computedFontFamily = fontStyle?.fontfamily || 'Georgia, serif';
  const computedFontSize = fontStyle?.fontsize || '10px';

  return (
    <div style={{ fontFamily: computedFontFamily, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
      
      {/* Header Section */}
      <div className="print-header">
        {isheader && image && (
          <div style={{ width: '100%' }}>
            <img
              className="logo"
              src={`data:image/png;base64,${image}`}
              alt="Lab Logo"
              style={{ width: '100%', height: '130px', objectFit: 'contain' }}
            />
          </div>
        )}

        <div
          className="print-Patient-Details"
          style={{
            fontFamily: computedFontFamily,
            padding: '10px',
            fontSize: computedFontSize,
            fontWeight: 'bold',
          }}
        >
          <Grid container className="P-Info" spacing={1}>
            {/* Left Patient Info */}
            <Grid item xs={5}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '4px' }}>
                <span style={{ fontWeight: 'bold' }}>MR No:</span>
                <span>{patient?.patientID || 'N/A'}-{sessionStorage.getItem('LabID') || '-'}</span>

                <span style={{ fontWeight: 'bold' }}>Patient Name:</span>
                <span>{patient?.patientName || 'N/A'}</span>

                <span style={{ fontWeight: 'bold' }}>
                  {patient?.sex?.toLowerCase() === 'male' ? 'Father Name:' : 'Father / Husband:'}
                </span>
                <span>{patient?.fatherOrHusbandName || '-'}</span>

                <span style={{ fontWeight: 'bold' }}>Age | Gender:</span>
                <span>{patient?.age ? `${patient.age} Years` : '-'} | {patient?.sex || '-'}</span>

                <span style={{ fontWeight: 'bold' }}>Address:</span>
                <span>{patient?.address || '-'}</span>
              </div>
            </Grid>

            {/* Middle Patient Info */}
            <Grid item xs={4}>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px' }}>
                <span style={{ fontWeight: 'bold' }}>Phone Number:</span>
                <span>{patient?.contactNumber || '-'}</span>

                <span style={{ fontWeight: 'bold' }}>Referred By:</span>
                <span>{patient?.referredBy || 'Self'}</span>

                <span style={{ fontWeight: 'bold' }}>Registration Date:</span>
                <span>{patient?.createdAt ? moment(patient.createdAt).format('D MMM YYYY') : '-'}</span>
              </div>
            </Grid>

            {/* Right QR Code Side */}
            <Grid item xs={3} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {qrCodeValue && (
                <div style={{ textAlign: 'center' }}>
                  <QRCode value={qrCodeValue} size={90} />
                  <div className="common-FontSize-small" style={{ fontSize: '9px', marginTop: '4px' }}>
                    Scan to Download
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
        <hr style={{ marginTop: '8px', marginBottom: '8px' }} />
      </div>

      {/* Main Report Content */}
      <div className="print-content">
        {Object.keys(groupedData).map((categoryKey, index) => {
          const categoryObj = groupedData[categoryKey];
          let subCategoryKeys = Object.keys(categoryObj.subCategories);

          // Standardize specific subcategory ordering for Urine Examination Reports
          if (categoryObj.categoryValue?.toUpperCase() === "URINE EXAMINATION REPORT") {
            const order = ["PHYSICAL EXAMINATION", "CHEMICAL EXAMINATION", "MICROSCOPIC EXAMINATION"];
            subCategoryKeys.sort((a, b) => {
              const subCategoryA = categoryObj.subCategories[a].subCategoryValue.toUpperCase();
              const subCategoryB = categoryObj.subCategories[b].subCategoryValue.toUpperCase();
              const indexA = order.indexOf(subCategoryA);
              const indexB = order.indexOf(subCategoryB);
              return (indexA === -1 ? order.length : indexA) - (indexB === -1 ? order.length : indexB);
            });
          }

          return (
            <div
              key={index}
              style={{
                fontFamily: computedFontFamily,
                padding: '10px 0',
                fontSize: computedFontSize,
              }}
            >
              <h4 className="common-FontWeight" style={{ textAlign: 'center', margin: '8px 0', textTransform: 'uppercase' }}>
                {categoryObj.categoryValue}
              </h4>

              {subCategoryKeys.map((subCategoryKey, subIndex) => {
                const subCategoryObj = categoryObj.subCategories[subCategoryKey];

                return (
                  <div key={subIndex} style={{ marginBottom: '12px' }}>
                    {subCategoryObj.subCategoryValue && (
                      <div className="common-FontWeight" style={{ fontSize: '11px', marginBottom: '4px' }}>
                        {subCategoryObj.subCategoryValue}
                      </div>
                    )}

                    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                      <thead style={{ backgroundColor: '#f8f8ff' }}>
                        <tr>
                          <th style={{ width: '40%', textAlign: 'left', padding: '6px', borderBottom: '1px solid #ccc' }}>Test Description</th>
                          <th style={{ width: '20%', textAlign: 'left', padding: '6px', borderBottom: '1px solid #ccc' }}>Normal Range</th>
                          <th style={{ width: '20%', textAlign: 'left', padding: '6px', borderBottom: '1px solid #ccc' }}>Unit</th>
                          <th style={{ width: '20%', textAlign: 'left', padding: '6px', borderBottom: '1px solid #ccc' }}>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subCategoryObj.items.map((data, itemIndex) => (
                          <React.Fragment key={itemIndex}>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '6px' }}>{data.testName}</td>
                              <td style={{ padding: '6px' }}>{formatTextWithLineBreaks(data.normalRange)}</td>
                              <td style={{ padding: '6px' }}>{formatTextWithLineBreaks(data.unit)}</td>
                              <td style={{ padding: '6px', fontWeight: 'bold' }}>{data.testResult}</td>
                            </tr>
                            {data.comment && data.comment.trim() !== '' && (
                              <tr>
                                <td colSpan={4} style={{ paddingLeft: '20px', paddingBottom: '6px', fontStyle: 'italic', fontSize: '9px', color: '#555' }}>
                                  Comment: {data.comment}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer Section */}
      <div
        className="print-footer"
        style={{
          fontFamily: computedFontFamily,
          marginTop: '20px',
        }}
      >
        <hr style={{ marginTop: 0, marginBottom: '8px' }} />

        {Comment && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{ fontWeight: 'bold', fontSize: '11px' }}>Report Comment</span>
            <div style={{ border: '1px solid lightgray', padding: '8px', borderRadius: '4px', backgroundColor: '#fafafa', marginTop: '4px' }}>
              <p style={{ margin: 0, fontSize: '10px' }}>{Comment}</p>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#555', marginBottom: '8px' }}>
          <span>
            <span>Printed at: {moment(currentDateTime).format('DD-MMM-YYYY hh:mm A')}</span> &nbsp;|&nbsp; 
            <span>Printed By: {username || 'System Admin'}</span>
          </span>
        </div>

        <div style={{ textAlign: 'center', fontSize: '9px', color: '#777', marginTop: '8px' }}>
          <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} {labName}. All rights reserved.</p>
        </div>
      </div>

    </div>
  );
};

export default Template2;